import { EventCodes } from '../App/model/event-codes';
import type { IWindowService } from 'angular';
import type { RoutingService } from '../App/services/mx-routing-service';
import type { EventService } from '../App/services/event-service';
import type { MxApplicationService } from '../App/services/mx-application-service';
import type { ConfigService } from '../App/services/mx-config-service';
import type { DataQueryService } from '../App/services/data-query-service';
import type { NavigationCalculationBadge, NavigationItem } from './model/navigation-item';
import type { RoutingItem } from '../App/model/routing-item';
import type { UserProfileService } from '../App/services/user-profile-service';
import { utils } from '../Utils/mx-utils.js';
import { NavigationItemImplementation } from '../Shell/PandoraSolutionBuilderAgent/model/enumerations';

export interface NavigationProvider {
	load: (param: boolean, loadOnly: boolean) => Promise<NavigationItem[]>;
}

type NavigationItemWithBadge = NavigationItem & Required<Pick<NavigationItem, 'calculationBadge'>>;

/**
 * @ngdoc service
 * @name mx.shell.NavigationService
 * @module mx.shell
 *
 * @requires {@ref mx.shell.NavigationProviderService mx.shell.NavigationProviderService}
 * @requires {@ref mx.shell.RoutingService mx.shell.RoutingService}
 * @requires {@ref mx.shell.EventService mx.shell.EventService}
 *
 * @description
 * A service prepares navigation items and provides method to get them
 */
export class MxNavigationService {
	private navigationProviderService: NavigationProvider = { load: async () => [] };
	public navItems: NavigationItem[] = [];
	public partiallyOpenedItem?: NavigationItem;
	private navigationLoaderPromise?: Promise<NavigationItem[]>;
	private navItemsWithAmountBadges: NavigationItemWithBadge[] = [];
	private navItemsWithAmountBadgesOnlyCollapsed: NavigationItemWithBadge[] = [];
	public openSettingHandler?: () => void;
	private recalculationAmountInterval?: NodeJS.Timeout;

	static readonly $inject = [
		'$window',
		'mx.shell.RoutingService',
		'mx.shell.EventService',
		'mx.shell.AppsService',
		'mx.SolutionBuilderAgent.DataQueryService',
		'mx.shell.Config',
		'mx.shell.UserProfileService',
	];

	constructor(
		private readonly $window: IWindowService,
		private readonly routingService: RoutingService,
		private readonly eventService: EventService,
		private readonly appsService: MxApplicationService,
		private readonly dataQueryService: ReturnType<typeof DataQueryService>,
		private readonly configService: ConfigService,
		private readonly userProfileService: UserProfileService
	) {}

	init() {
		this.eventService.subscribeGlobal(EventCodes.BeforeNavigationItemClickHandler, (event, navItem) => {
			// ARIA: empty Page Title before URL is changed to prevent Screen-Reader from reading previous title several times while URL is changing
			if (navItem.id !== this.currentItem?.id && !navItem.selected) {
				document.title = '‐'; // silent symbol for Screen Reader to prevent reading URL if Internet is slow
			}
		});
		this.eventService.subscribeGlobal(EventCodes.RouteActivated, () => this.setNavigationItemFromRoute());
		this.eventService.subscribeGlobal(EventCodes.NavigationReload, () => this.get(true));
	}

	private _currentItem?: NavigationItem;
	get currentItem() {
		return this._currentItem;
	}

	set currentItem(value) {
		this.setCurrentNavigationItem(value);
	}

	public setNavigationProvider(navigationProvider: NavigationProvider) {
		this.navigationProviderService = navigationProvider;
	}

	public async getNavigationItemFromRoute(routeNavigationId: string, routeStructures: string) {
		let navItem = await this.getNavigationByRouteNavigationId(routeNavigationId);
		if (navItem?.structure && navItem?.showStructureInNavigation && routeStructures) {
			const ids = routeStructures.split('_');
			const children = await this.loadChildren(navItem);
			const structureNavItem = await this.findDynamicChild(children, navItem.id, ids);
			if (structureNavItem) {
				navItem = structureNavItem;
			}
		}
		return navItem;
	}

	private async getNavigationByRouteNavigationId(routeNavigationId: string): Promise<NavigationItem | undefined> {
		const items = await this.get();
		const comparer = (item: NavigationItem) => {
			return item.parameters?.navigationId?.toLowerCase() === routeNavigationId;
		};
		return this.getItem(items, comparer);
	}

	private async setNavigationItemFromRoute() {
		let item;

		const currentState = this.routingService.getCurrent();

		if (currentState.parameters.navigationId) {
			item = await this.getNavigationItemFromRoute(currentState.parameters.navigationId, currentState.parameters.structures);
		} else {
			const items = await this.get();
			item = this.findCurrent(items);
		}

		if (item !== this.currentItem) {
			await this.setCurrentNavigationItem(item, true);
		}
	}

	public async loadChildren(item: NavigationItem) {
		if (item.childrenDynamicLoader && !item.children?.length) {
			return await item.childrenDynamicLoader();
		} else {
			return item.children || [];
		}
	}

	private async findDynamicChild(children: NavigationItem[], parenId: string, ids: string[]): Promise<NavigationItem | undefined> {
		if (Array.isArray(children) && Array.isArray(ids) && ids.length > 0) {
			const found = this.getItemById(children, `${ids[ids.length - 1]}_${parenId}`);
			if (!found) {
				return;
			}

			if (ids.length > 1) {
				const children2 = await this.loadChildren(found);
				return await this.findDynamicChild(children2, found.id, ids.splice(0, ids.length - 1));
			} else {
				return found;
			}
		}
		return;
	}

	private findCurrent(items: NavigationItem[]) {
		const comparer = (item: NavigationItem) => !!item.route && this.routingService.isCurrent(item.route, item.parameters);
		return this.getItem(items, comparer);
	}

	public async getNavigationItem(id: string) {
		const items = await this.get();
		return await this.getNavigationItemById(items, id);
	}

	public async getNavigationItems(ids: string[]) {
		const navigationItems = [];

		if (Array.isArray(ids)) {
			for (const id of ids) {
				const navigationItem = await this.getNavigationItem(id);
				if (navigationItem) {
					navigationItems.push(navigationItem);
				}
			}
		}
		return navigationItems;
	}

	private async getNavigationItemById(items: NavigationItem[] = [], id: string): Promise<NavigationItem | undefined> {
		if (id.includes('_')) {
			const ids = id.split('_');
			const parent = this.getItemById(items, ids[ids.length - 1]);
			if (!parent) {
				return;
			}
			const children = await this.loadChildren(parent);
			return this.findDynamicChild(children, parent.id, ids.splice(0, ids.length - 1));
		} else {
			return this.getItemById(items, id);
		}
	}

	private getItemById(items: NavigationItem[], id: string) {
		const comparer = (item: NavigationItem) => item.id.toLowerCase() === id.toLowerCase();
		return this.getItem(items, comparer);
	}

	public findItem(comparer: (item: NavigationItem) => boolean): NavigationItem | undefined {
		return this.getItem(this.navItems, comparer);
	}

	public findAllItems(comparer: (item: NavigationItem) => boolean): NavigationItem[] {
		return this.getItems(this.navItems, comparer);
	}

	private getItem(items: NavigationItem[], comparer: (item: NavigationItem) => boolean) {
		let found: NavigationItem | undefined;

		items.some((item) => {
			if (comparer(item)) {
				found = item;
				return true;
			} else if (Array.isArray(item.children)) {
				found = this.getItem(item.children, comparer);
				if (found) {
					return true;
				}
			}
			return false;
		});

		return found;
	}

	private getItems(navItems: NavigationItem[], comparer: (item: NavigationItem) => boolean, accumulator: NavigationItem[] = []) {
		navItems.forEach((item) => {
			if (comparer(item)) {
				accumulator.push(item);
			}
			if (item.children?.length) {
				this.getItems(item.children, comparer, accumulator);
			}
		});
		return accumulator;
	}

	/**
	 * @ngdoc method
	 * @name mx.shell.NavigationService#get
	 *
	 * @param {boolean} reload - If true - forces navigation items to be loaded from {@ref mx.shell.NavigationProviderService navigationProviderService}.
	 *
	 * @description
	 * Gets navigation items from cache or loads it from {@ref mx.shell.NavigationProviderService navigationProviderService}.
	 *
	 * @returns {promise} Resolves as an array of {@ref mx.shell.NavigationItem mx.shell.NavigationItem}.
	 */
	public get(reload = false) {
		if (!this.navigationLoaderPromise || reload) {
			this.navigationLoaderPromise = this.loadNavigationItems();
		}
		return this.navigationLoaderPromise;
	}

	public async reload() {
		this.navigationLoaderPromise = this.loadNavigationItems();
		await this.navigationLoaderPromise;
	}

	private async loadNavigationItems() {
		const loadedNavData = await this.navigationProviderService.load(true, true);
		this.navItems = this.mergeNavItems(this.navItems, loadedNavData);

		await Promise.all(this.navItems.map((navItem) => navItem.childrenDynamicLoader?.()));

		this.navItems.forEach((item, i) => this.handleItem(item, i));
		const defaultRoute = await this.getDefaultRoute(this.navItems);
		this.eventService.dispatchEvent(EventCodes.NavigationLoaded, {
			navItems: this.navItems,
			routes: this.getRoutesFromNavigationItems(this.navItems),
			defaultRoute,
		});

		if (this.currentItem) {
			const newCurrentNavItem = await this.getNavigationItemById(this.navItems, this.currentItem.id);
			if (newCurrentNavItem) {
				await this.setCurrentNavigationItem(newCurrentNavItem, true);
			} else {
				this.currentItem = undefined;
			}
		}

		this.initCalculationBadges();
		return this.navItems;
	}

	private mergeNavItems(originNav: NavigationItem[], loadedNav: NavigationItem[]) {
		originNav = this._mergeNavItemsAddingAndEditing(originNav, loadedNav);
		originNav = this._mergeNavItemsDeleting(originNav, loadedNav);
		return originNav;
	}

	private _mergeNavItemsAddingAndEditing(originNav: NavigationItem[] = [], loadedNav: NavigationItem[] = [] /*, originParent */) {
		loadedNav.forEach((loadedNavItem) => {
			const originNavItem = originNav.find((originNavItem) => originNavItem.id === loadedNavItem.id);
			if (originNavItem?.childrenDynamicLoader) {
				originNav[originNav.indexOf(originNavItem)] = loadedNavItem;
			} else if (originNavItem) {
				originNavItem.title = loadedNavItem.title;
				this._mergeNavItemsAddingAndEditing(originNavItem.children, loadedNavItem.children);
			} else {
				originNav.push(loadedNavItem);
			}
		});
		return originNav;
	}

	private _mergeNavItemsDeleting(originNav: NavigationItem[] = [], loadedNav: NavigationItem[] = []) {
		if (originNav.length > loadedNav.length) {
			for (let i = originNav.length - 1; i > -1; i--) {
				if (!loadedNav.some((loadedItem) => loadedItem.id === originNav[i].id)) {
					originNav.splice(i, 1);
				}
			}
		}
		originNav.forEach((originNavItem) => {
			const loadedNavItem = loadedNav.find((loadedNavItem) => {
				return loadedNavItem.id === originNavItem.id;
			});
			if (loadedNavItem) {
				this._mergeNavItemsDeleting(originNavItem.children, loadedNavItem.children);
			}
		});
		return originNav;
	}

	private handleItem(navItem: NavigationItem, index: number, parent?: NavigationItem) {
		// Set flag `hasChildren` if navigation-item already has an array of `children`
		// Or if those `children` can be loaded from server in future (`navItem.childrenDynamicLoader` is set)
		navItem.children = Array.isArray(navItem.children) ? navItem.children : [];
		navItem.hasChildren = Boolean(navItem.children?.length || navItem.childrenDynamicLoader);
		navItem.index = index;
		navItem.level = parent ? parent.level + 1 : 0;
		if (navItem.level > 0) {
			navItem.selected = false;
		}
		navItem.parent = parent;
		navItem.showNestedNavigationInTabs = (navItem.level === 0 && navItem.showNestedNavigationInTabs) || (navItem.level === 1 && !parent?.showNestedNavigationInTabs);

		navItem.children.forEach((child, i) => {
			this.handleItem(child, i, navItem);
		});
	}

	private getRoutesFromNavigationItems(items: NavigationItem[]) {
		const routes = this.getRoutesFromNavigationItemsRecursive(items);
		return Object.keys(routes).map((routeName) => routes[routeName]);
	}

	private getRoutesFromNavigationItemsRecursive(items: NavigationItem[] = []) {
		const routes: Record<string, RoutingItem> = {};
		items.forEach(({ routingItem, children }) => {
			if (routingItem && typeof routingItem === 'object') {
				const name = routingItem.name;
				routes[name] = routes[name] || routingItem;
			}
			const childrenRoutes = this.getRoutesFromNavigationItemsRecursive(children);
			Object.keys(childrenRoutes).forEach((childRouteName) => {
				routes[childRouteName] = routes[childRouteName] || childrenRoutes[childRouteName];
			});
		});

		return routes;
	}

	private getClosestRoutedNavItem(item: NavigationItem): NavigationItem | undefined {
		if (!item) {
			return;
		}

		if (item.routingItem) {
			return item;
		}

		if (item.children && item.children.length > 0) {
			return this.getClosestRoutedNavItem(item.children[0]);
		}

		return;
	}

	/**
	 * @async
	 * @ngdoc method
	 * @name mx.shell.NavigationService#getDefaultNavigationItem
	 *
	 * @description
	 * Get Default Navigation Items.
	 *
	 * @returns {Promise<NavigationItem>}
	 */
	private async getDefaultNavigationItem(navItems: NavigationItem[]) {
		const currentApp = this.appsService.getCurrentApplication();
		const defaultApplicationNavigationItem = currentApp.navigationItem && (await this.getNavigationItemById(navItems, currentApp.navigationItem));

		if (defaultApplicationNavigationItem) {
			return defaultApplicationNavigationItem;
		}

		// Find first item that's NOT an external URL
		const firstRoutableItem = navItems.find((item) => item.implementationType !== NavigationItemImplementation.ExternalUrl);

		if (firstRoutableItem) {
			return this.getClosestRoutedNavItem(firstRoutableItem);
		}

		// Fallback if all items are external URLs (edge case)
		console.warn('⚠️ All navigation items are external URLs, using first item anyway');
		return this.getClosestRoutedNavItem(navItems[0]);
	}

	private async getDefaultRoute(navItems: NavigationItem[]) {
		const item = await this.getDefaultNavigationItem(navItems);
		if (!item) {
			return;
		}

		return {
			name: item.route || '',
			parameters: item.parameters,
		};
	}

	private async goToRoute(item: NavigationItem) {
		if (/^https?:\/\//.test(item.route || '')) {
			this.$window.location.href = item.route as string;
		} else {
			await this.routingService.go(item.route, item.parameters);
		}
	}

	public async setNavigationItemById(navigationId: string) {
		const navigationItem = await this.getNavigationItem(navigationId);
		await this.setCurrentNavigationItem(navigationItem);
	}

	public removeItem(id: string, parent?: NavigationItem): boolean {
		if (parent) {
			if (Array.isArray(parent.children)) {
				const index = parent.children.findIndex((n) => n.id === id);
				if (index > -1) {
					parent.children.splice(index, 1);
					return true;
				}
				return parent.children.some((n) => this.removeItem(id, n));
			}
			return false;
		} else {
			const index = this.navItems.findIndex((n) => n.id === id);
			if (index > -1) {
				this.navItems.splice(index, 1);
				return true;
			}
			return this.navItems.some((n) => this.removeItem(id, n));
		}
	}

	public async setCurrentNavigationItem(navigationItem?: NavigationItem, ignoreActivation = false) {
		if (!navigationItem?.id) {
			return;
		}
		if (navigationItem === this.currentItem) {
			this.setActiveStatus(this.currentItem, true);

			if (!ignoreActivation) {
				const isCurrent = this.routingService.isCurrent(navigationItem.route ?? '', navigationItem.parameters);
				if (!isCurrent) {
					await this.activateNavigationItem(navigationItem);
				}
			}

			return;
		}

		await this.appsService.setWindowTitle(navigationItem?.title);

		const activatingPromise = ignoreActivation ? Promise.resolve() : this.activateNavigationItem(navigationItem);
		const previousItem = this.currentItem;

		await activatingPromise;
		await this.loadChildren(navigationItem);
		this.setActiveStatus(this.partiallyOpenedItem, false);
		this.partiallyOpenedItem = undefined;
		if (navigationItem.route) {
			this.setActiveStatus(this.currentItem, false);
			this._currentItem = navigationItem;
			this.setActiveStatus(this.currentItem, true);

			this.eventService.dispatchEvent(EventCodes.CurrentNavigationItemChanged, {
				navigationItem: this.currentItem,
				previousItem,
			});
		} else if (!this.isAncestor(navigationItem)) {
			this.partiallyOpenedItem = navigationItem;
			this.setActiveStatus(navigationItem, true);
		}
	}

	private isAncestor(navItem: NavigationItem): boolean {
		let current = this.currentItem;
		while (current) {
			if (current === navItem) {
				return true;
			}
			current = current.parent;
		}
		return false;
	}

	private setActiveStatus(navItem?: NavigationItem, state = false) {
		let item = navItem;
		while (item) {
			item.current = state;
			item.selected = state;
			item.parentOfActivated = false;
			if (item.level === 0 && item.hasChildren) {
				item.unwrapped = item.selected;
			}
			item = item.parent;
		}

		if (navItem) {
			navItem.activated = state && !!navItem.route;

			if (navItem.activated && navItem.parent) {
				navItem.parent.parentOfActivated = true;
			}
		}
	}

	private async activateNavigationItem(item: NavigationItem) {
		if (item?.route && !this.routingService.isCurrent(item.route, item.parameters)) {
			if (item.openInNewWindow) {
				this.$window.open(item.route);
			} else {
				await this.goToRoute(item);
			}
		}
	}

	public markFilterTextMatched(navItems: NavigationItem[] = [], filterText: string, showAllChildren: boolean, checkJustVisible?: boolean) {
		let filterTextMatchedItem: NavigationItem | undefined;
		let showAllChildrenForJustFoundItem: boolean;

		if (Array.isArray(navItems)) {
			navItems.forEach((item) => {
				if (!item._titleLowerCase) {
					item._titleLowerCase = item.title.toLowerCase();
				}

				item.unwrapped = false;

				if ((!checkJustVisible || item.matchesFilterCriteria !== false) && filterText && item._titleLowerCase.includes(filterText)) {
					item.matchesFilterCriteria = true;
					filterTextMatchedItem = item;
					showAllChildrenForJustFoundItem = true;
				} else {
					item.matchesFilterCriteria = showAllChildren || !filterText;
				}

				this.markFilterTextMatched(item.children, filterText, showAllChildren || showAllChildrenForJustFoundItem);
				showAllChildrenForJustFoundItem = false;
			});
		}

		if (filterTextMatchedItem) {
			this._expandAllParents(filterTextMatchedItem);
			filterTextMatchedItem = undefined;
		}
	}

	private _expandAllParents({ parent }: NavigationItem) {
		if (parent) {
			parent.matchesFilterCriteria = true;
			parent.unwrapped = true;
			this._expandAllParents(parent);
		}
	}

	private getAllChildren(items: NavigationItem[] = []): NavigationItem[] {
		let children: NavigationItem[] = [];
		items.forEach((item) => {
			if (item.children) {
				children = children.concat(item.children);
			}
		});

		if (children.length) {
			children = children.concat(this.getAllChildren(children));
		}

		return children;
	}

	private getFlatArrayOfNavItems() {
		return (this.navItems || []).concat(this.getAllChildren(this.navItems));
	}

	private initCalculationBadges() {
		this.navItemsWithAmountBadges = this.getFlatArrayOfNavItems().filter((item) => item.calculationBadge) as NavigationItemWithBadge[];
		this.navItemsWithAmountBadgesOnlyCollapsed = this.getRelatedBadges();
		this.initRecalculationAmountBadgeInterval();
		this.initRecalculationAmountBadgeSubscribers();
		this.recalculateAmountBadges(true);
	}

	/**
	 * Returns array of "related" badges on root navigation items which have one or more children with badges.
	 * They are used to show the dot in collapsed mode when of children has badge with value
	 */
	private getRelatedBadges() {
		const rootItems: NavigationItemWithBadge[] = [];
		this.navItemsWithAmountBadges.forEach((navigationItem) => {
			if (navigationItem.parent) {
				let root: NavigationItem = navigationItem;
				while (root.parent) {
					root = root.parent;
				}

				if (!root.calculationBadge) {
					root.calculationBadge = {
						onlyCollapsed: true,
						relatedBadges: [navigationItem.calculationBadge],
						isNumeric: false,
						title: '',
						value: navigationItem.calculationBadge.value ? ' ' : '',
					};
					navigationItem.calculationBadge.relatedBadges = [root.calculationBadge];
					rootItems.push(root as NavigationItemWithBadge);
				} else if (root.calculationBadge.onlyCollapsed) {
					root.calculationBadge.relatedBadges = root.calculationBadge.relatedBadges || [];
					root.calculationBadge.relatedBadges.push(navigationItem.calculationBadge);
				}
			}
		});
		return rootItems;
	}

	private initRecalculationAmountBadgeInterval() {
		const badgeRefreshTime = this.configService.get('navigationBadgeRefreshTime');
		let nextRecalculationTime = Date.now() + badgeRefreshTime;
		if (this.recalculationAmountInterval) {
			clearInterval(this.recalculationAmountInterval);
		}
		this.recalculationAmountInterval = setInterval(() => {
			const time = Date.now();
			if (time >= nextRecalculationTime) {
				this.recalculateAmountBadges();
				nextRecalculationTime = time + badgeRefreshTime;
			}
		}, 5000);
	}

	private initRecalculationAmountBadgeSubscribers() {
		this.eventService.subscribeGlobal(EventCodes.ViewDataLoaded, (e, view) => {
			if (view && this.currentItem?.calculationBadge?.value) {
				this.recalculateAmountBadge(this.currentItem);
			}
		});
	}

	private async recalculateAmountBadge(navigationItem: NavigationItem): Promise<boolean> {
		if (!navigationItem.calculationBadge?.filter) {
			return false;
		}
		const params = {
			filters: navigationItem.calculationBadge.filter,
			pageSize: navigationItem.calculationBadge.isNumeric ? 9001 : 1,
		};
		const amount = await this.dataQueryService.getItemsAmount(navigationItem.contentWidgetId, params);
		const oldAmount = navigationItem.calculationBadge.amount ?? 0;
		navigationItem.calculationBadge.amount = amount;

		if (navigationItem.calculationBadge.isNumeric) {
			navigationItem.calculationBadge.value = amount < 1000 ? amount : amount > 9000 ? '9K+' : `${Math.floor(amount / 1000)}K${amount % 1000 === 0 ? '' : '+'}`;
		} else {
			navigationItem.calculationBadge.value = amount === 0 ? '' : ' ';
		}

		if (navigationItem.calculationBadge.relatedBadges?.length) {
			this.recalculateRelatedBadge(navigationItem.calculationBadge.relatedBadges[0]);
		}

		return oldAmount < amount;
	}

	private recalculateRelatedBadge(badge: NavigationCalculationBadge) {
		badge.value = (badge.relatedBadges || []).every((badge) => !badge.value) ? '' : ' ';
	}

	private async recalculateAmountBadges(init: boolean = false) {
		const result = await Promise.all(this.navItemsWithAmountBadges.map((item) => this.recalculateAmountBadge(item)));

		const modified = result.reduce((a, b) => a || b, false);

		this.navItemsWithAmountBadgesOnlyCollapsed.forEach((navigationItem) => {
			this.recalculateRelatedBadge(navigationItem.calculationBadge);
		});

		if (modified && !init) {
			await this.playNotificationSound();
		}
	}

	private async playNotificationSound() {
		const userProfile = await this.userProfileService.get();

		const soundPath = userProfile.notificationSoundPath;

		if (soundPath) {
			await utils.playSound(soundPath);
		}
	}

	public getTabsLevelItem(navigationItem: NavigationItem) {
		let navItem: NavigationItem | undefined = navigationItem;
		let topNavItemWithTabs: NavigationItem | undefined;
		do {
			if (navItem.showNestedNavigationInTabs) {
				topNavItemWithTabs = navItem;
			}
			navItem = navItem.parent;
		} while (navItem);

		return topNavItemWithTabs;
	}
}
